// screens/Auth/PasswordReset.tsx

import React, { useState, useEffect } from "react";
import { 
  View, 
  Text, 
  StyleSheet, 
  Alert, 
  KeyboardAvoidingView, 
  ScrollView, 
  Platform
} from "react-native";
import { useColors, ThemeColors } from '../../hooks/useColors';
import { useTranslation } from "react-i18next";
import { useNavigation } from "@react-navigation/native";
import { supabase } from "../../lib/supabase";

import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { validatePassword } from "../../helpers/validators";
import { spacing, typography } from "../../theme";
import Toast from 'react-native-toast-message';

function PasswordReset() {
  const colors = useColors();
  const styles = createStyles(colors);
  const { t } = useTranslation();
  const navigation = useNavigation();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Log when component mounts
  useEffect(() => {
    console.log('🔐 [PASSWORD-RESET-SCREEN] Component mounted');
    
    // Log current session state for debugging
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('🔐 [PASSWORD-RESET-SCREEN] Current session:', { 
        hasSession: !!session, 
        user: session?.user?.email
      });
    });
  }, []);

  const handleSubmit = async () => {
    console.log('🔐 [PASSWORD-RESET-SCREEN] handleSubmit called', { 
      hasNewPassword: !!newPassword, 
      hasConfirmPassword: !!confirmPassword,
      passwordsMatch: newPassword === confirmPassword
    });
    
    if (!newPassword || !confirmPassword) {
      console.log('🔐 [PASSWORD-RESET-SCREEN] Validation failed: empty fields');
      Alert.alert(t("auth.error"), "Please fill in all fields");
      return;
    }

    if (!validatePassword(newPassword)) {
      console.log('🔐 [PASSWORD-RESET-SCREEN] Validation failed: invalid password');
      Alert.alert("Invalid Password", "Password must be at least 8 characters long and contain letters and numbers.");
      return;
    }

    if (newPassword !== confirmPassword) {
      console.log('🔐 [PASSWORD-RESET-SCREEN] Validation failed: passwords dont match');
      Alert.alert(t("auth.passwordMismatch"), "Passwords do not match");
      return;
    }

    console.log('🔐 [PASSWORD-RESET-SCREEN] Validation passed, updating password...');
    setLoading(true);

    try {
      // Update the user's password
      console.log('🔐 [PASSWORD-RESET-SCREEN] Calling supabase.auth.updateUser...');
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('🔐 [PASSWORD-RESET-SCREEN] Password update error:', error);
        Alert.alert('Error', error.message || 'Failed to update password');
        setLoading(false);
        return;
      }
      
      console.log('🔐 [PASSWORD-RESET-SCREEN] Password updated successfully!');

      // Clear the password reset flow flag and set completed flag
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // Set completed flag FIRST - this prevents any navigation back to PasswordReset
        sessionStorage.setItem('passwordResetCompleted', 'true');
        console.log('🔐 [PASSWORD-RESET-SCREEN] Set passwordResetCompleted flag');
        
        sessionStorage.removeItem('passwordResetFlow');
        console.log('🔐 [PASSWORD-RESET-SCREEN] Cleared passwordResetFlow flag from sessionStorage');
        
        // Notify other tabs that password reset is completed via localStorage
        localStorage.setItem('jadetrade_password_reset_completed', Date.now().toString());
        console.log('🔐 [PASSWORD-RESET-SCREEN] Notified other tabs about password reset completion');
        
        // Clean up the localStorage keys after a short delay
        setTimeout(() => {
          localStorage.removeItem('jadetrade_password_reset');
          localStorage.removeItem('jadetrade_password_reset_completed');
        }, 1000);
      }

      // Clear form
      setNewPassword("");
      setConfirmPassword("");

      // Sign out the user so they can log in with their new password
      console.log('🔐 [PASSWORD-RESET-SCREEN] Signing out user after password update');
      await supabase.auth.signOut();

      // Show success toast
      Toast.show({
        type: 'success',
        text1: 'Password Updated',
        text2: 'Please log in with your new password.'
      });

      // Navigate to Auth screen
      // Since we signed out, MainSwitchNavigator will show AuthFlow automatically
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // Redirect immediately to prevent React from re-rendering and navigating back to PasswordReset
        // The toast will briefly show before the redirect
        window.location.href = '/';
      } else {
        // On native, navigation will automatically show Auth since user is signed out
        // @ts-ignore
        navigation.navigate('Auth');
      }

    } catch (err: any) {
      console.error('Password reset error:', err);
      Alert.alert('Error', err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.contentContainer}>
          <Text style={styles.title}>Change Password</Text>
          <Text style={styles.subtitle}>Choose a new password for your account.</Text>

          <Card>
            <Input
              label={t("auth.newPassword")}
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry
              autoCapitalize="none"
              placeholder="Enter new password"
              autoCorrect={false}
            />
            <Input
              label={t("auth.confirmNewPassword")}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry
              autoCapitalize="none"
              placeholder="Confirm new password"
              autoCorrect={false}
            />
            
            {/* Password requirements */}
            <Text style={styles.requirements}>
              Password must be at least 8 characters and contain letters and numbers.
            </Text>

            <Button
              onPress={() => {
                console.log('🔐 [PASSWORD-RESET-SCREEN] Button onPress triggered!');
                handleSubmit();
              }}
              loading={loading}
              style={styles.bottomButton}
              disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword}
            >
              Update Password
            </Button>
          </Card>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

export default PasswordReset;

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: "center",
  },
  contentContainer: {
    padding: spacing.md,
  },
  title: {
    ...typography.title,
    fontSize: 22,
    marginBottom: spacing.sm + 4,
    textAlign: "center",
    color: colors.text,
  },
  subtitle: {
    ...typography.body,
    marginBottom: spacing.lg,
    textAlign: "center",
    color: colors.textLight,
  },
  requirements: {
    ...typography.body,
    fontSize: 14,
    color: colors.textLight,
    textAlign: "center",
    marginTop: spacing.sm,
    marginBottom: spacing.md,
    fontStyle: "italic",
  },
  bottomButton: {
    marginTop: spacing.md,
  },
});